import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BiLoaderAlt } from 'react-icons/bi';
import { FaInfoCircle, FaFilter, FaEye, FaTrash } from 'react-icons/fa';
import { MdComment } from 'react-icons/md';
import { AnimatePresence } from 'framer-motion';
import FilterModal from './FilterModal';
import CandidateRemarks from './CandidateRemarks';
import { CandidateDetailsModal } from './ModalComponents';
import { API_ENDPOINTS } from '../apiConfig';

const CandidateManagement = () => {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    city: '',      // Program
    center: '',    // Profile
    status: '',    // Status
    gender: ''     // Gender
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [statusLoading, setStatusLoading] = useState({});
  const [candidatesPerPage, setCandidatesPerPage] = useState(10); // Changed to state
  const [loading, setLoading] = useState(true);
  const [selectedCandidateForDetails, setSelectedCandidateForDetails] = useState(null);
  const [selectedCandidateForRemarks, setSelectedCandidateForRemarks] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [uniqueFilterValues, setUniqueFilterValues] = useState({
    states: [],
    cities: [],
    centers: [],
    skills: [],
    statuses: ['New', 'Hold', 'Selected', 'Rejected']
  });
  const role = localStorage.getItem('role'); // Get user role

 // Add delete handler
 const handleDeleteResume = async (candidateId) => {
  try {
    await axios.delete(`${API_ENDPOINTS.GET_RESUMES}/${candidateId}`);
    
    // Update both candidates lists
    const updateCandidates = (prevCandidates) => 
      prevCandidates.filter(candidate => candidate._id !== candidateId);
    
    setCandidates(updateCandidates);
    setFilteredCandidates(updateCandidates);
    
    toast.success('Resume deleted successfully');
  } catch (error) {
    toast.error('Failed to delete resume: ' + (error.message || 'Unknown error'));
  }
};

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_ENDPOINTS.GET_RESUMES);
      setCandidates(response.data);
      setFilteredCandidates(response.data);
    } catch (error) {
      toast.error('Error fetching candidates: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Updated handleStatusChange function
  const handleStatusChange = async (candidateId, newStatus) => {
    setStatusLoading(prev => ({ ...prev, [candidateId]: true }));
    try {
      const response = await axios.patch(
        `${API_ENDPOINTS.GET_RESUMES}/${candidateId}/status`,
        { status: newStatus }
      );
      
      // Update only the specific candidate in both states
      const updateCandidate = (prevCandidates) => {
        return prevCandidates.map(candidate =>
          candidate._id === candidateId
            ? { ...candidate, ...response.data }
            : candidate
        );
      };

      setCandidates(updateCandidate);
      setFilteredCandidates(updateCandidate);
      
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status: ' + (error.message || 'Unknown error'));
    } finally {
      setStatusLoading(prev => ({ ...prev, [candidateId]: false }));
    }
  };

  const updateUniqueFilterValues = useCallback(() => {
    if (candidates.length === 0) return;

    const states = [...new Set(candidates.map(c => c.nativeState))].filter(Boolean).sort();
    const cities = [...new Set(candidates.map(c => c.cityId?.name))].filter(Boolean).sort();
    const centers = [...new Set(candidates.map(c => c.centerId?.name))].filter(Boolean).sort();
    const skills = [...new Set(candidates.flatMap(c => c.skillsCategory || []))].filter(Boolean).sort();

    setUniqueFilterValues(prev => ({
      ...prev,
      states,
      cities,
      centers,
      skills
    }));
  }, [candidates]);

  const applyFilters = useCallback(() => {
    let filtered = [...candidates];

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        filtered = filtered.filter(candidate => {
          if (key === 'city') {
            return candidate.cityId?.name === value;
          }
          if (key === 'center') {
            return candidate.centerId?.name === value;
          }
          if (key === 'skillsCategory') {
            return candidate.skillsCategory?.includes(value);
          }
          if (key === 'status') {
            return candidate.status === value;
          }
          return candidate[key] === value;
        });
      }
    });

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(candidate =>
        candidate.candidateName.toLowerCase().includes(query)
      );
    }

    setFilteredCandidates(filtered);
  }, [candidates, filters, searchQuery]);


  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  useEffect(() => {
    updateUniqueFilterValues();
  }, [updateUniqueFilterValues]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      gender: '',
      type: '',
      nativeState: '',
      city: '',
      center: '',
      skillsCategory: '',
      status: '', // Added status
    });
  }, []);

  const handleViewResume = async (candidateId) => {
    try {
      const viewResumeURL = `${API_ENDPOINTS.GET_RESUMES}/${candidateId}/view`;
      window.open(viewResumeURL, '_blank');
    } catch (error) {
      console.error('Error viewing resume:', error);
      toast.error('Unable to view resume. Please try again later.');
    }
  };
  const totalPages = useMemo(() => 
    Math.ceil(filteredCandidates.length / candidatesPerPage),
    [filteredCandidates.length, candidatesPerPage]
  );

  // Ensure current page is valid after filtering
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [currentPage, totalPages]);

  // Get current page candidates
  const paginatedCandidates = useMemo(() => 
    filteredCandidates.slice(
      (currentPage - 1) * candidatesPerPage,
      currentPage * candidatesPerPage
    ),
    [filteredCandidates, currentPage, candidatesPerPage]
  );


  const handlePageSizeChange = (newSize) => {
    setCandidatesPerPage(Number(newSize));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <BiLoaderAlt className="animate-spin text-4xl text-[#fbba00]" />
      </div>
    );
  }

  // Get status color class
  const getStatusColorClass = (status) => {
    switch (status) {
      case 'Selected':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'Hold':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };
  

  
  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-[#080366]">
            Candidates <span className="text-[#fbba00]">Management</span>
          </h1>
          <span className="text-sm text-gray-500">
            Total Candidates: {filteredCandidates.length}
          </span>
        </div>

        {/* Search and Filter Section */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <div className="relative w-full md:w-1/2">
            <input
              type="text"
              placeholder="Search by name..."
              className="w-full pl-4 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-[#fbba00] focus:border-transparent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button
            onClick={() => setFilterModalOpen(true)}
            className="bg-[#fbba00] text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-[#e5aa00] transition-colors"
          >
            <FaFilter />
            Filters
            {Object.values(filters).some(value => value) && (
              <span className="bg-white text-[#fbba00] rounded-full w-5 h-5 flex items-center justify-center text-xs">
                {Object.values(filters).filter(value => value).length}
              </span>
            )}
          </button>
        </div>

        {/* Active Filters Display */}
        {Object.values(filters).some(value => value) && (
          <div className="mb-4 flex flex-wrap gap-2">
            {Object.entries(filters).map(([key, value]) => {
              if (!value) return null;
              return (
                <span
                  key={key}
                  className="bg-[#fbba00] bg-opacity-10 text-[#fbba00] px-3 py-1 rounded-full text-sm flex items-center gap-2"
                >
                  {key.replace(/([A-Z])/g, ' $1').trim()}: {value}
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, [key]: '' }))}
                    className="hover:text-red-500"
                  >
                    ×
                  </button>
                </span>
              );
            })}
            <button
              onClick={handleClearFilters}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Candidates Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#fbba00] uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#fbba00] uppercase tracking-wider">Program</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#fbba00] uppercase tracking-wider">Profile</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#fbba00] uppercase tracking-wider">Skills</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#fbba00] uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#fbba00] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedCandidates.map(candidate => (
                  <tr key={candidate._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => setSelectedCandidateForDetails(candidate)}
                        className="text-[#080366] hover:text-[#fbba00] flex items-center gap-2 transition-colors"
                      >
                        {candidate.candidateName}
                        <FaInfoCircle className="text-sm" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {candidate.cityId?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {candidate.centerId?.name || '-'}
                    </td>
                   
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {candidate.skillsCategory?.map(skill => (
                          <span
                            key={skill}
                            className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {statusLoading[candidate._id] ? (
                          <BiLoaderAlt className="animate-spin text-[#fbba00]" />
                        ) : (
                          <select
                            value={candidate.status}
                            onChange={(e) => handleStatusChange(candidate._id, e.target.value)}
                            className={`rounded-full px-3 py-1 text-sm font-medium ${getStatusColorClass(candidate.status)} border-0 cursor-pointer focus:ring-2 focus:ring-[#fbba00]`}
                          >
                            {uniqueFilterValues.statuses.map(status => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => handleViewResume(candidate._id)}
                          className="text-[#080366] hover:text-[#fbba00] transition-colors flex items-center gap-2"
                          title="View Resume"
                        >
                          <FaEye className="text-lg" />
                          <span className="text-sm hidden md:inline">View</span>
                        </button>
                        
                        <button
                          onClick={() => setSelectedCandidateForRemarks(candidate)}
                          className="text-[#080366] hover:text-[#fbba00] transition-colors"
                          title="Add Remarks"
                        >
                          <MdComment />
                        </button>

                        {role === 'superadmin' && (
          <button
            onClick={() => handleDeleteResume(candidate._id)}
            className="text-red-600 hover:text-red-800 transition-colors flex items-center gap-2"
            title="Delete Resume"
          >
            <FaTrash className="text-lg" />
          </button>
        )}
                      </div>  
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* No Results Message */}
        {filteredCandidates.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No candidates found matching your criteria
          </div>
        )}

        {/* Pagination */}
        {filteredCandidates.length > 0 && (
        <div className="flex flex-col md:flex-row justify-between items-center mt-6 gap-4">
          <div className="flex items-center gap-4">

            <div className="flex items-center gap-2">
              <label htmlFor="pageSize" className="text-sm text-gray-600">
                Show:
              </label>
              <select
                id="pageSize"
                value={candidatesPerPage}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                className="border cursor-pointer rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-[#fbba00] focus:border-transparent"
              >
                <option  value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              className="px-4 py-2 border rounded-lg cursor-pointer disabled:opacity-50 hover:bg-[#b88904] bg-[#fbba00]  text-white transition-colors"
            >
              Previous
            </button>
            <div className="flex items-center gap-2">
              {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                const pageNumber = i + 1;
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      currentPage === pageNumber
                        ? 'bg-gray-300 text-black'
                        : 'border hover:bg-gray-50'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
              className="px-4 py-2 border rounded-lg cursor-pointer disabled:opacity-50 hover:bg-[#b88904] bg-[#fbba00] text-white transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
        {/* Modals */}
        <AnimatePresence>
          {filterModalOpen && (
            <FilterModal
              isOpen={filterModalOpen}
              onClose={() => setFilterModalOpen(false)}
              filters={filters}
              setFilters={setFilters}
              uniqueValues={uniqueFilterValues}
              onClearFilters={handleClearFilters}
            />
          )}
          {selectedCandidateForDetails && (
            <CandidateDetailsModal
              isOpen={!!selectedCandidateForDetails}
              onClose={() => setSelectedCandidateForDetails(null)}
              candidate={selectedCandidateForDetails}
            />
          )}
        </AnimatePresence>       
       {selectedCandidateForRemarks && (
          <CandidateRemarks
            candidateId={selectedCandidateForRemarks.id}
            candidateName={selectedCandidateForRemarks.name}
            onClose={() => setSelectedCandidateForRemarks(null)}
          />
        )}
      </div>
    </div>
  );
};

export default CandidateManagement;

